import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { MonacoJsonEditor } from '../components/MonacoJsonEditor';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import AdminPage from './AdminPage';
import type { 
  ConfigDetailsResponse,
  ConfigUpdateRequest,
  ConfigUpdateResponse,
  ConfigResetResponse
} from '@services/config/types';

const AdminConfigEditPage: React.FC = () => {
  const { configName } = useParams<{ configName: string }>();
  const navigate = useNavigate();
  
  const [config, setConfig] = useState<ConfigDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<string>('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    if (configName) {
      loadConfig();
    }
  }, [configName]);

  const loadConfig = async () => {
    if (!configName) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.post<ConfigDetailsResponse>('/admin/config/get', {
        name: configName,
      });
      
      setConfig(response);
      setEditedData(JSON.stringify(response.data, null, 2));
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!configName || !config) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Parse and validate JSON
      let parsedData;
      try {
        parsedData = JSON.parse(editedData);
      } catch (parseError) {
        setError('Invalid JSON format. Please check your syntax.');
        return;
      }

      const updateRequest: ConfigUpdateRequest = {
        name: configName,
        data: parsedData,
      };

      await apiClient.post<ConfigUpdateResponse>('/admin/config/update', updateRequest);
      
      setSuccess('Configuration updated successfully!');
      
      // Reload the config to show updated values
      await loadConfig();
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!configName) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await apiClient.post<ConfigResetResponse>('/admin/config/reset', {
        name: configName,
      });
      
      setSuccess('Configuration reset to defaults successfully!');
      setResetDialogOpen(false);
      
      // Reload the config to show default values
      await loadConfig();
    } catch (err) {
      console.error('Error resetting config:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/configs');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Configuration not found</Alert>
      </Box>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Edit Configuration: {config.name}
          </Typography>
          <Button variant="outlined" onClick={handleBack}>
            Back to Configs
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Config Info */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Configuration Details</Typography>
                  <Chip
                    label={config.isCustomized ? 'Customized' : 'Using Defaults'}
                    color={config.isCustomized ? 'primary' : 'default'}
                    variant={config.isCustomized ? 'filled' : 'outlined'}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {config.description}
                </Typography>
                {config.lastModified && (
                  <Typography variant="body2" color="text.secondary">
                    Last modified: {new Date(config.lastModified).toLocaleString()}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* JSON Editor */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Current Configuration
                </Typography>
                <MonacoJsonEditor
                  value={editedData}
                  onChange={setEditedData}
                  height={400}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Edit the JSON configuration above
                </Typography>
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => setResetDialogOpen(true)}
                    disabled={saving || !config.isCustomized}
                  >
                    Reset to Defaults
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Default Values */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Default Configuration
                </Typography>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: 'grey.50',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    maxHeight: '400px',
                    overflow: 'auto',
                  }}
                >
                  {JSON.stringify(config.defaultData, null, 2)}
                </Paper>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Read-only view of default values
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
          <DialogTitle>Reset Configuration</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to reset "{config.name}" to its default values? 
              This will permanently delete any customizations you've made.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleReset} color="warning" variant="contained">
              Reset to Defaults
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
};

export default AdminConfigEditPage;